import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Grid, Row, Col, TextCol } from "components/Grid/Grid";
import Page from "components/Page/Page";
import BlogPageFooter from "./BlogPageFooter";

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  color: var(--text-muted);
  margin-bottom: 2rem;
  
  a {
    color: var(--text-muted);
    border-bottom: 1px solid transparent;
    transition: color 0.2s ease;
    
    &:hover {
      color: var(--accent);
      border-bottom-color: currentColor;
    }
  }

  span.active-crumb {
    color: var(--text-primary);
    font-weight: 700;
  }
`;

const Separator = styled.span`
  color: var(--text-muted);
  opacity: 0.5;
  margin: 0 0.5rem;
  font-weight: 300;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 2rem;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: var(--accent);
  border-bottom: 1px solid transparent;

  &:hover {
    color: var(--accent-hover);
    border-bottom-color: currentColor;
  }
`;

const CaseStudyCallout = styled.aside`
  margin: 0 0 2rem;
  padding: 1.5rem;
  background: #e7f6f6;
  color: var(--text-primary);
  font-family: "IBM Plex Serif", Georgia, serif;
  font-size: 1.125rem;
  line-height: 1.6;

  p {
    margin: 0;
    color: inherit;

    &:not(:last-child) {
      margin-bottom: 1rem;
    }
  }
`;

const CalloutLabel = styled.div`
  margin-bottom: 0.75rem;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`;

const CaseStudyMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: -0.75rem 0 0.75rem;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: var(--text-muted);

  span.dot {
    opacity: 0.5;
  }
`;

const CaseStudyTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  margin: 0 0 1.5rem;

  span {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--tag-color);
  }
`;

const MarkdownWrapper = styled.div`
  font-family: "IBM Plex Serif", Georgia, serif;
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--text-primary);

  h1, h2, h3, h4, h5, h6 {
    font-family: "IBM Plex Sans", sans-serif;
    color: var(--text-primary);
    font-weight: 700;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    line-height: 1.3;
    letter-spacing: -0.02rem;
  }

  h1 { 
    font-size: 2.5rem; 
    margin-top: 0;
    margin-bottom: 1.5rem;
    line-height: 1.15;
  }
  
  h2 { 
    font-size: 1.75rem; 
    border-bottom: 1px solid var(--border-color); 
    padding-bottom: 0.5rem; 
    margin-top: 3rem;
  }
  
  h3 { 
    font-size: 1.35rem; 
    margin-top: 2.25rem;
  }

  p {
    margin-bottom: 1rem;
    color: var(--text-primary);
  }

  a {
    color: var(--accent);
    border-bottom: 1px solid transparent;
    font-weight: 600;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--accent-hover);
      border-bottom: 1px solid var(--accent-hover);
    }
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    
    li {
      padding-bottom: 0.4rem;
      line-height: 1.6;
      color: var(--text-primary);
    }
  }

  blockquote {
    border-left: 3px solid var(--accent);
    margin: 1.75rem 0;
    padding-left: 1.25rem;
    font-style: italic;
    color: var(--text-muted);
    font-family: "IBM Plex Serif", serif;
    
    p {
      color: var(--text-muted);
      margin-bottom: 0;
    }
  }

  img {
    border: none;
    margin: 2.5rem 0;
    width: 100%;
    height: auto;
    box-sizing: border-box;
    display: block;
    border-radius: 4px;
  }

  button.article-image {
    position: relative;
    display: block;
    width: 100%;
    margin: 2.5rem 0;
    padding: 0;
    overflow: hidden;
    border: none;
    background: transparent;
    cursor: zoom-in;

    img {
      margin: 0;
    }

    span {
      position: absolute;
      right: 1rem;
      bottom: 1rem;
      padding: 0.4rem 0.6rem;
      background: var(--text-primary);
      color: var(--bg-main);
      font-family: "IBM Plex Sans", sans-serif;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.05rem;
      text-transform: uppercase;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover span,
    &:focus-visible span {
      opacity: 1;
    }
  }

  pre {
    background: #f8fafc;
    border: 1px solid var(--border-color);
    padding: 1.25rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 1.75rem 0;
    
    code {
      background: transparent;
      border: none;
      padding: 0;
      color: var(--text-primary);
      font-family: monospace;
      font-size: 0.9rem;
    }
  }
`;

const ImageModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(15, 23, 42, 0.9);

  img {
    max-width: 100%;
    max-height: 100%;
    border: none;
  }

  button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    border: none;
    background: transparent;
    color: white;
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05rem;
    text-transform: uppercase;
    cursor: pointer;
  }
`;

export default function BlogPage(props) {
  const [article, setArticle] = useState("");
  const [expandedImage, setExpandedImage] = useState(null);
  let urlString = window.location.origin + "/content/" + props.markdownFile;
  
  useEffect(() => {
    const getData = () => {
      fetch(urlString)
        .then((response) => response.text())
        .then((result) => setArticle(result))
        .catch((err) => console.error("Error loading markdown:", err));
    };
    getData();
  }, [urlString]);

  const [width, setWidth] = useState(window.innerWidth);
  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    if (!expandedImage) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setExpandedImage(null);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [expandedImage]);

  const pathSegments = props.markdownFile.split("/").filter(Boolean);
  const sectionName = pathSegments[0] || "portfolio";
  const headingMatch = article.match(/^(# .+)\r?\n+/);
  const articleHeading = headingMatch ? headingMatch[1] : "";
  const articleBody = headingMatch
    ? article.slice(headingMatch[0].length)
    : article;
  const calloutParagraphs = Array.isArray(props.callout)
    ? props.callout
    : [props.callout];
  const markdownComponents = {
    img: ({ src, alt }) => (
      <button
        type="button"
        className="article-image"
        onClick={() => setExpandedImage({ src, alt })}
        aria-label={`Expand ${alt || "image"}`}
      >
        <img src={src} alt={alt || ""} />
        <span>Expand image</span>
      </button>
    ),
  };
  const imageModal = expandedImage && (
    <ImageModal
      role="dialog"
      aria-modal="true"
      aria-label={expandedImage.alt || "Expanded image"}
      onClick={() => setExpandedImage(null)}
    >
      <button type="button" onClick={() => setExpandedImage(null)}>
        Close
      </button>
      <img
        src={expandedImage.src}
        alt={expandedImage.alt || ""}
        onClick={(event) => event.stopPropagation()}
      />
    </ImageModal>
  );

  if (props.embedded) {
    return (
      <article>
        <BackLink to="/" onClick={() => window.scrollTo(0, 0)}>
          ← See all projects
        </BackLink>
        {articleHeading && (
          <MarkdownWrapper>
            <ReactMarkdown children={articleHeading} components={markdownComponents} />
          </MarkdownWrapper>
        )}
        {(props.date || props.company) && (
          <CaseStudyMeta>
            {props.date && <span>{props.date}</span>}
            {props.date && props.company && <span className="dot">•</span>}
            {props.company && <span>{props.company}</span>}
          </CaseStudyMeta>
        )}
        {props.tags?.length > 0 && (
          <CaseStudyTags>
            {props.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </CaseStudyTags>
        )}
        {props.callout && (
          <CaseStudyCallout>
            <CalloutLabel>Project summary</CalloutLabel>
            {calloutParagraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </CaseStudyCallout>
        )}
        <MarkdownWrapper>
          <ReactMarkdown children={articleBody} components={markdownComponents} />
        </MarkdownWrapper>
        <BlogPageFooter />
        {imageModal}
      </article>
    );
  }

  return (
    <Page currentPage={props.currentPage.toLowerCase()}>
      <Grid width="100%">
        <Row>
          {width >= 768 ? (
            <>
              <Col size={1}></Col>
              <TextCol>
                <Breadcrumb>
                  <a href="/">home</a>
                  <Separator>/</Separator>
                  <a href="/2026">{sectionName}</a>
                  <Separator>/</Separator>
                  <span className="active-crumb">{props.currentPage}</span>
                </Breadcrumb>
                <MarkdownWrapper>
                  <ReactMarkdown children={article} components={markdownComponents} />
                </MarkdownWrapper>
                <BlogPageFooter />
              </TextCol>
              <Col size={1}></Col>
            </>
          ) : (
            <Col size={1}>
              <Breadcrumb>
                <a href="/">home</a>
                <Separator>/</Separator>
                <span className="active-crumb">{props.currentPage}</span>
              </Breadcrumb>
              <MarkdownWrapper>
                <ReactMarkdown children={article} components={markdownComponents} />
              </MarkdownWrapper>
              <BlogPageFooter />
            </Col>
          )}
        </Row>
      </Grid>
      {imageModal}
    </Page>
  );
}